import { useCallback, useMemo } from "react";
import { trpc } from "../trpc";

// This hook models the allowlist/denylist strategy being migrated to a new
// open/standard/managed/blocked control-level model:
// https://gitlab.com/zapier/engineering-index/-/merge_requests/2136
// Revisit isAppRestricted's decision logic once that ships.
export function useControlledAppAccess({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const { data, isLoading, isError } =
    trpc.account.controlledAppAccess.useQuery(undefined, {
      enabled,
      // Controlled-app restrictions for the user change rarely; avoid refetching (up
      // to 25 paginated monolith requests) on every mount/window focus.
      staleTime: 1000 * 60 * 5,
    });

  const strategy = data?.strategy ?? null;

  const deniedApis = useMemo(
    () =>
      new Set(
        (data?.apps ?? [])
          .filter((app) => app.status === "denied")
          .map((app) => app.selectedApi),
      ),
    [data?.apps],
  );

  const allowedApis = useMemo(
    () =>
      new Set(
        (data?.apps ?? [])
          .filter((app) => app.status === "allowed")
          .map((app) => app.selectedApi),
      ),
    [data?.apps],
  );

  // Controlled apps are versionless (e.g. "SlackAPI"), matching
  // the domain_filter guard's app-id convention.
  const isAppRestricted = useCallback(
    (appId: string): boolean => {
      const strippedId = appId?.split("@")[0] ?? "";
      if (!strippedId || !strategy) return false;

      if (strategy === "denylist") {
        return deniedApis.has(strippedId);
      }

      return !allowedApis.has(strippedId);
    },
    [strategy, deniedApis, allowedApis],
  );

  return {
    isAppRestricted,
    strategy,
    isLoading,
    isError,
  };
}
